import type { SortConfig, TableColumn } from './types';

/**
 * Sort data based on the current sort configuration
 * @param data The data to sort
 * @param sortConfig The sort configuration
 * @param columns The column definitions
 * @returns Sorted data
 */
export const sortData = <T>(
  data: T[],
  sortConfig: SortConfig | null,
  columns: TableColumn<T>[]
): T[] => {
  if (!sortConfig) return data;

  const { field, direction } = sortConfig;
  const column = columns.find(col => col.field === field);

  return [...data].sort((a, b) => {
    // Get values accounting for nested paths (e.g., 'user.name')
    const aValue = getValueByPath(a, field as string);
    const bValue = getValueByPath(b, field as string);

    // Use valueFormatter if available
    const formattedA = column?.valueFormatter ? column.valueFormatter(aValue, a) : aValue;
    const formattedB = column?.valueFormatter ? column.valueFormatter(bValue, b) : bValue;

    // Sort logic
    if (formattedA === null || formattedA === undefined) return direction === 'asc' ? -1 : 1;
    if (formattedB === null || formattedB === undefined) return direction === 'asc' ? 1 : -1;

    // String comparison
    if (typeof formattedA === 'string' && typeof formattedB === 'string') {
      return direction === 'asc'
        ? formattedA.localeCompare(formattedB)
        : formattedB.localeCompare(formattedA);
    }

    // Number comparison
    if (typeof formattedA === 'number' && typeof formattedB === 'number') {
      return direction === 'asc' ? formattedA - formattedB : formattedB - formattedA;
    }

    // Date comparison
    if (formattedA instanceof Date && formattedB instanceof Date) {
      return direction === 'asc'
        ? formattedA.getTime() - formattedB.getTime()
        : formattedB.getTime() - formattedA.getTime();
    }

    // Default comparison for other types
    return direction === 'asc'
      ? String(formattedA).localeCompare(String(formattedB))
      : String(formattedB).localeCompare(String(formattedA));
  });
};

/**
 * Get value from an object by path (supports nested paths like 'user.name')
 * @param obj The object to get value from
 * @param path The path to the value
 * @returns The value at the path
 */
export const getValueByPath = <T>(obj: T, path: string): unknown => {
  // Handle direct property access
  if (typeof path !== 'string' || path === '') {
    return obj;
  }

  // Handle nested paths
  const keys = path.split('.');
  return keys.reduce((acc, key) => {
    if (acc === null || acc === undefined) return acc;
    return (acc as Record<string, unknown>)[key];
  }, obj as unknown);
};

/**
 * Calculate pagination results
 * @param data The data to paginate
 * @param page Current page
 * @param rowsPerPage Rows per page
 * @returns Paginated data
 */
export const paginateData = <T>(data: T[], page: number, rowsPerPage: number): T[] => {
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  return data.slice(startIndex, endIndex);
};

/**
 * Get CSS class for table row based on index (for striping)
 * @param index Row index
 * @returns CSS class
 */
export const getRowClassName = (index: number): string => {
  return index % 2 === 0 ? 'even-row' : 'odd-row';
};
